import React from 'react';
import { IWeatherContext } from './types';
import { WeatherContentEntity } from './entities';
import { ACCESS_TOKEN_KEY, RECONNECT_TIME, WEATHER_SOCKET_URL } from '@src/core/config';
import { useGetUserDataQuery } from '@src/core/features';
import { trackError } from '@src/core/sentry';

export const WeatherContext = React.createContext<IWeatherContext>({});

const WeatherProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const socket = React.useRef<WebSocket>();
  const reconnectTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUnmountedRef = React.useRef(false);
  const [data, setData] = React.useState<WeatherContentEntity>();
  const { data: userData } = useGetUserDataQuery();
  const hubId = userData?.hub?.id;

  const clearReconnectTimer = React.useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
  }, []);

  const cleanupSocket = React.useCallback((ws: WebSocket | undefined | null) => {
    if (!ws) return;
    ws.onopen = null;
    ws.onclose = null;
    ws.onerror = null;
    ws.onmessage = null;
    try {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    } catch {
      // noop
    }
  }, []);

  const createSocket = React.useCallback(() => {
    if (isUnmountedRef.current) return;
    if (!hubId) return;

    const prev = socket.current;
    if (prev) cleanupSocket(prev);

    const hubURL = `?hubId=${hubId}`;
    const ws = new WebSocket(WEATHER_SOCKET_URL + hubURL);
    socket.current = ws;

    ws.onopen = () => {
      if (isUnmountedRef.current) {
        cleanupSocket(ws);
        return;
      }
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      ws.send(JSON.stringify({ type: 'AUTH', token }));
    };

    ws.onclose = () => {
      if (isUnmountedRef.current) return;
      clearReconnectTimer();
      reconnectTimer.current = setTimeout(() => {
        reconnectTimer.current = null;
        createSocket();
      }, RECONNECT_TIME);
    };

    ws.onmessage = (event) => {
      try {
        setData(JSON.parse(event.data) as WeatherContentEntity);
      } catch (error: unknown) {
        console.error('Failed to parse weather data:', event.data);
        trackError(error instanceof Error ? error : new Error(String(error)), {
          code: 'FE_0014',
          extra: { rawLength: typeof event.data === 'string' ? event.data.length : undefined },
        });
      }
    };
  }, [hubId, cleanupSocket, clearReconnectTimer]);

  React.useEffect(() => {
    isUnmountedRef.current = false;
    createSocket();

    return () => {
      isUnmountedRef.current = true;
      clearReconnectTimer();
      cleanupSocket(socket.current);
      socket.current = undefined;
    };
  }, [createSocket, cleanupSocket, clearReconnectTimer]);

  const value = React.useMemo<IWeatherContext>(() => ({ data }), [data]);

  return (
    <WeatherContext.Provider value={value}>
      {children}
    </WeatherContext.Provider>
  );
};

export default WeatherProvider;
